import React, { useState } from "react";

import { useApolloClient } from "@apollo/client";
import { DeviceInfo } from "@/interface/deviceInfo";
import fetchDoctorsApi from "@/redux/slice/doctors/apis/fetchRecommendedDoctors";
import Image from "@/components/Image";
import SectionHeader from "@/components/SectionHeader";
import TabsComponent from "@/components/TabsComponent";
import DoctorCardsList from "@/components/DoctorsWidget/components/DoctorCardsList";
import { DoctorDetails } from "@/redux/slice/doctors/types/FetchDoctorsPayload";
import ButtonLink from "@/components/ButtonLink";
import styles from "./DoctorsWidget.module.scss";

interface DoctorsWidgetProps {
  userAgent?: DeviceInfo;
  data?: any;
  currentSpeciality: string;
  doctors: DoctorDetails[];
  utm_medium?: string;
}

interface DoctorsStateInterface {
  currentTab: string;
  currentSpecialistsUrl: string;
  doctorsList: any[];
  isFetchingDoctors: boolean;
}

const DoctorsWidget: React.FC<DoctorsWidgetProps> = (props) => {
  const { userAgent, data, currentSpeciality, doctors, utm_medium } = props;

  const logoUrl = data?.Logo?.data?.attributes?.formats?.thumbnail?.url;
  const allSpecialistsUrl = data?.Specialities?.length > 0 ? data?.Specialities[0]["ViewOurSpecialistsUrl"] : "";

  const apolloClient = useApolloClient();
  const [doctorsState, setDoctorsState] = useState<DoctorsStateInterface>({
    currentTab: currentSpeciality,
    currentSpecialistsUrl: allSpecialistsUrl,
    doctorsList: doctors,
    isFetchingDoctors: false,
  });

  const updateDoctorsState = (payload: any) => {
    setDoctorsState((prevState) => ({ ...prevState, ...payload }));
  };

  const specialitiesArray = data?.Specialities?.map((item: any): any => {
    return {
      name: item?.SpecialityName,
      slug: item?.SpecialityId,
      ViewOurSpecialistsUrl: item?.ViewOurSpecialistsUrl,
    };
  });

  const fetchProducts = async (specialtyId: string) => {
    const currentSpecialtyObj = specialitiesArray?.find((item: any) => item.slug === specialtyId);
    const allSpecialtiesUrl = currentSpecialtyObj?.ViewOurSpecialistsUrl || "";
    updateDoctorsState({ currentTab: specialtyId, isFetchingDoctors: true, currentSpecialistsUrl: allSpecialtiesUrl });
    const payload = {
      apolloClient: apolloClient,
      specialtyId: specialtyId,
    };
    const fetchDoctorsResponse = await fetchDoctorsApi(payload);

    if (fetchDoctorsResponse?.data?.getDoctorList) {
      updateDoctorsState({
        doctorsList: fetchDoctorsResponse?.data?.getDoctorList?.doctors,
        isFetchingDoctors: false,
      });
    } else {
      updateDoctorsState({
        doctorsList: [],
        isFetchingDoctors: false,
      });
    }
  };

  const collectionDataTabs =
    specialitiesArray?.length > 0
      ? specialitiesArray.map((item: any) => {
          return {
            ...item,
            content: (
              <DoctorCardsList
                doctors={doctorsState?.doctorsList}
                userAgent={userAgent}
                logoUrl={logoUrl}
                utm_medium={utm_medium}
              />
            ),
          };
        })
      : [];

  const handleDataOnTabChange = (slug: string) => {
    fetchProducts(slug);
  };

  return (
    <div className={`${styles.doctorsWidgetRoot}`}>
      <div className="container">
        <div className={styles.doctorsWidgetHeader}>
          <Image src={data?.Logo?.data?.attributes?.url} alt="Logo" height={70} width={70} className={styles.topLogo} />
          <SectionHeader title={data?.Title} subTitle={data?.Description} userAgent={userAgent} />
        </div>
        <TabsComponent
          tabItems={collectionDataTabs}
          activeItem={doctorsState?.currentTab}
          onTabChange={(slug: any) => handleDataOnTabChange(slug)}
          isTabAlignLeft={false}
          rootClass={styles.doctorsWidgetTabsRoot}
        />
        <ButtonLink href={doctorsState?.currentSpecialistsUrl} className={"btn"} color="primarybaby">
          View All Specialists
        </ButtonLink>
      </div>
    </div>
  );
};

export default DoctorsWidget;
